/**
 * Règles de réservation de la fiche cours v2 (docs/ux-v2/SPEC.md § 3.6, § 4.2, § 6.2 règle 2).
 *
 * Fonctions pures, reprises À L'IDENTIQUE de l'écran v1 `screens/Class/index.tsx` (non modifié) :
 * chaque bloc cite sa source. Aucun accès à `window.mondayMindbody` ici : les réglages sont passés
 * en paramètre (testable sous vitest, environnement node).
 */
import type { MindbodyClass } from 'types/class';
import type { ClientService } from 'types/client';

/* ------------------------------------------------------------------ crédits */

/** = Class/index.tsx : événement « RIISE 5 ANS » (Location.Name), forcé à 2 crédits. */
export const RIISE_5ANS_RE = /^\s*riise\s*5\s*ans\s*$/i;

type ClassLike = Pick<MindbodyClass, 'ClassDescription' | 'Location'> | null | undefined;

/** = Class/index.tsx `creditsRequired` : NumDeducted (min 1), 2 pour l'événement RIISE 5 ANS. */
export function creditsRequiredFor(cls: ClassLike): number {
  const sessionTypeNumDeducted = cls?.ClassDescription?.SessionType?.NumDeducted ?? 1;
  const isRiise5AnsEvent = RIISE_5ANS_RE.test(cls?.Location?.Name ?? '');
  return Math.max(1, sessionTypeNumDeducted, isRiise5AnsEvent ? 2 : 1);
}

/** = Class/index.tsx `isCommunityClass` (revalidé côté serveur, même expression). */
export function isCommunityClassName(name: string | null | undefined): boolean {
  return /communauté|communaute/i.test(name ?? '');
}

/** = Class/index.tsx `isParisSite` : les trois sites Paris (Dynamo, RIISE, Punch). */
export const PARIS_SITE_IDS = ['5736526', '5736555', '5736551'];
/** = Class/index.tsx : programme Mindbody du Run Club. */
export const RUN_CLUB_PROGRAM_ID = 46;

/** = Class/index.tsx `isRunClub` : réservation atomique `POST class/book-runclub`. */
export function isRunClubClass(cls: Pick<MindbodyClass, 'ClassDescription'> | null | undefined, siteId: string | undefined) {
  return PARIS_SITE_IDS.includes(String(siteId ?? '')) && cls?.ClassDescription?.Program?.Id === RUN_CLUB_PROGRAM_ID;
}

/**
 * = Class/index.tsx `bookClass()` : forfaits « routine » (challenge) dont l'usage masque ensuite les
 * forfaits challenge dans le choix des crédits.
 */
export const ROUTINE_SERVICE_IDS = [49537, 51802, 21630, 12867];

/** = Class/index.tsx `noRoutineServices` / `clientServices` selon `hasAlreadyUsedRoutineService`. */
export function eligibleServices(services: ClientService[], hasAlreadyUsedRoutineService: boolean): ClientService[] {
  if (!hasAlreadyUsedRoutineService) return services;
  return services.filter((service) => !service.Name.toLowerCase().includes('challenge'));
}

const expirationTime = (service: ClientService) => {
  const time = service.ExpirationDate ? new Date(service.ExpirationDate).getTime() : NaN;
  return Number.isFinite(time) ? time : Number.POSITIVE_INFINITY;
};

/**
 * Forfait présélectionné dans le panneau (SPEC FC6, proposition) : parmi ceux qui ont assez de
 * crédits, celui qui expire le premier ; à défaut, le premier de la liste (= comportement v1 avec un
 * seul forfait : `data[0]`).
 */
export function defaultServiceId(services: ClientService[], needed: number): number | undefined {
  if (!services.length) return undefined;
  const enough = services.filter((service) => (service.Remaining ?? 0) >= needed);
  if (!enough.length) return Number(services[0].Id);
  return Number([...enough].sort((a, b) => expirationTime(a) - expirationTime(b))[0].Id);
}

/**
 * Forfait effectivement débité : celui choisi dans la liste s'il couvre `needed`, sinon le forfait présélectionné
 * pour `needed` (defaultServiceId). Revue du 05/10 : « Pack découverte » (1 crédit) choisi à la main, puis « Venez à
 * deux » (2 crédits) → « Crédits insuffisants » et « Acheter des crédits », alors que « 10 Séances » suffisait. Le
 * choix reste mémorisé : interrupteur éteint, le forfait choisi revient.
 */
export function effectiveServiceId(services: ClientService[], chosenId: number | undefined, needed: number): number | undefined {
  const chosen = services.find((service) => Number(service.Id) === chosenId);
  if (chosen && ((chosen.Remaining ?? 0) >= needed || !services.some((service) => (service.Remaining ?? 0) >= needed))) {
    return Number(chosen.Id);
  }
  return defaultServiceId(services, needed);
}

/* --------------------------------------------------------- corps de requête */

/** = Class/index.tsx `ReservationFormValues` (corps de POST class/book et class/book-course). */
export interface ReservationBody {
  SpotNumber?: number;
  serviceId?: number;
  classId: string;
  Waitlist?: boolean | number;
  isCommunityClass?: boolean;
  isEnrollment?: boolean;
}

/** = Class/index.tsx `bookClass()` : `isCourse ? '/class/book-course' : '/class/book'`. */
export function bookEndpoint(isCourse: boolean): '/class/book' | '/class/book-course' {
  return isCourse ? '/class/book-course' : '/class/book';
}

export interface BookBodyInput {
  classId: string;
  isEnrollmentInURL: boolean;
  /** Spot choisi (plan de salle). */
  spot?: number;
  serviceId?: number;
  /** Inscription sur la liste d'attente (cours complet). */
  isWaitlist?: boolean;
  /** Entrée de liste d'attente du client (place libérée à confirmer). */
  waitlistEntryId?: number;
  hasNoRoomLayout: boolean;
  isCourse: boolean;
}

/**
 * Corps de `POST class/book` (ou `book-course`) = Class/index.tsx `confirmServiceSelection()` puis
 * `bookClass()` : mêmes clés, dans le même ordre (`SpotNumber`, `classId`, `serviceId`, `Waitlist`,
 * puis `isEnrollment` ajouté par `bookClass`) ; les `undefined` disparaissent à la sérialisation.
 */
export function buildBookBody(input: BookBodyInput): ReservationBody {
  const noSpot = input.isEnrollmentInURL || !!input.isWaitlist || input.hasNoRoomLayout || input.isCourse;
  const body: ReservationBody = {
    SpotNumber: noSpot ? undefined : input.spot,
    classId: input.classId,
    serviceId: input.serviceId,
    Waitlist: input.isWaitlist ? true : undefined,
  };
  if (input.waitlistEntryId) body.Waitlist = input.waitlistEntryId;
  // = bookClass() : Object.assign(body, { classId: id }) ; body.isEnrollment = isEnrollmentInURL.
  Object.assign(body, { classId: input.classId });
  body.isEnrollment = input.isEnrollmentInURL;
  return body;
}

/** Corps d'un cours communauté = Class/index.tsx `onSubmit()` (pas de forfait, spot si choisi). */
export function buildCommunityBody(classId: string, isEnrollmentInURL: boolean, spot?: number): ReservationBody {
  const body: Partial<ReservationBody> = { isCommunityClass: true };
  if (spot) body.SpotNumber = spot;
  Object.assign(body, { classId });
  body.isEnrollment = isEnrollmentInURL;
  return body as ReservationBody;
}

/**
 * = Class/index.tsx `onSubmit()`, cours complet sans inscription en liste d'attente : trace Sentry du choix du
 * forfait (mêmes messages, tags et extras). La v2 choisit le forfait dans le panneau et non dans une modale, mais
 * garde le message « opening selection modal » pour que les recherches Sentry existantes couvrent les deux.
 */
export function waitlistServiceSelectionTrace(services: Array<Pick<ClientService, 'Id'>>, user: string, email?: string) {
  const isWaitlist = true;
  if (services.length === 1) {
    return {
      message: 'Single service available, confirming selection',
      context: {
        level: 'info' as const,
        tags: { category: 'service-selection' },
        extra: { isWaitlist, serviceId: services[0].Id, user, email },
      },
    };
  }
  return {
    message: 'Multiple services available, opening selection modal',
    context: {
      level: 'info' as const,
      tags: { category: 'service-selection' },
      extra: { isWaitlist, user, email },
    },
  };
}

/* ---------------------------------------------------------- choix des spots */

export interface SpotSelection {
  spot: number | null;
  guestOn: boolean;
  guestSpot: number | null;
}

export const EMPTY_SELECTION: SpotSelection = { spot: null, guestOn: false, guestSpot: null };

/**
 * Clic sur un spot libre (maquette /msc, action `spot`) : premier clic = mon spot ; « Venez à deux »
 * actif et mon spot choisi, un clic sur un autre spot = spot de l'invité ; re-clic = désélection.
 */
export function selectSpot(selection: SpotSelection, n: number): SpotSelection {
  if (selection.guestOn && selection.spot !== null && selection.spot !== n) {
    return { ...selection, guestSpot: selection.guestSpot === n ? null : n };
  }
  const spot = selection.spot === n ? null : n;
  return { ...selection, spot, guestSpot: selection.guestSpot === n ? null : selection.guestSpot };
}

/** Interrupteur « Venez à deux » : l'éteindre oublie le spot de l'invité. */
export function toggleGuest(selection: SpotSelection): SpotSelection {
  return selection.guestOn ? { ...selection, guestOn: false, guestSpot: null } : { ...selection, guestOn: true };
}

/* ------------------------------------------------------- fenêtre de résa */

/** Cours passé : il a commencé (aucune réservation possible, SPEC FC12). */
export function isPastClass(startDateTime: string, now: Date = new Date()): boolean {
  return new Date(startDateTime) <= now;
}

/**
 * Date d'ouverture des réservations d'un cours futur hors fenêtre = utils/functions.ts
 * `getBookableDays()` : la fenêtre glisse chaque jour à 12 h, de J (J−1 avant midi) à J + `days`.
 * Un cours du jour D devient réservable le jour D − `days`, à 12 h.
 */
export function bookingOpensAt(startDateTime: string, numberOfDays: number): Date {
  const start = new Date(startDateTime);
  const opens = new Date(start.getFullYear(), start.getMonth(), start.getDate() - numberOfDays, 12, 0, 0, 0);
  return opens;
}

/* ------------------------------------------------- relectures d'après réservation (retour du 07/10) */

/** = staleTime global du QueryClient (main.tsx) : une liste de forfaits plus jeune est celle que le client a sous les yeux. */
export const SERVICES_FRESH_MS = 60 * 1000;
/** Confirmation ouverte au plus tard 30 s après la réponse du serveur : sa relecture des crédits est celle de la réservation. */
export const BOOKED_RECENTLY_MS = 30 * 1000;

/** État d'une requête du cache React Query (QueryState, champs utiles). */
export interface CachedQuery<T> {
  data?: T;
  dataUpdatedAt: number;
  isInvalidated: boolean;
  fetchStatus: 'idle' | 'fetching' | 'paused';
}

/**
 * Forfaits relus avant la réservation (v1 `onSubmit`) : inutile quand la liste en cache, celle affichée dans le
 * panneau, a moins d'une minute, n'est ni invalidée ni en cours de lecture. Le serveur relit de toute façon les
 * forfaits (`ClientService::getClientServices`) et refuse un forfait qui ne s'applique plus.
 */
export function freshCachedServices<T>(cached: CachedQuery<T> | undefined, now: number): T | null {
  if (!cached?.data || cached.isInvalidated || cached.fetchStatus !== 'idle') return null;
  const age = now - cached.dataUpdatedAt;
  return age >= 0 && age < SERVICES_FRESH_MS ? cached.data : null;
}

/** Heure de la réservation si la confirmation s'ouvre juste après elle (sinon null : actualisation, retour plus tard). */
export function recentBookedAt(bookedAt: number | null | undefined, now: number): number | null {
  if (!bookedAt) return null;
  const age = now - bookedAt;
  return age >= 0 && age < BOOKED_RECENTLY_MS ? bookedAt : null;
}

/**
 * Spots du billet de confirmation. Le cours relu (réservations du client, membre d'abord) fait foi ; juste après la
 * réservation (`bookedAt` récent), les spots que le serveur vient d'accepter (état de navigation) s'y ajoutent tant que
 * le cours ne les porte pas : cours en cache d'avant la réservation, ou Mindbody pas encore à jour. Cas de la revue du
 * 08/10 : « Ajouter un invité » sur un cours déjà réservé, le cours en cache ne portait que le spot du membre, et la
 * confirmation annonçait une seule place pendant 2 à 3 s (« Ajouter à mon agenda » sans le spot de l'invité). Plus
 * tard (actualisation, retour), le cours seul, l'état de navigation n'étant qu'un repli.
 */
export function confirmationSpots(fromClass: number[], booked: number[] | undefined, bookedAt: number | null): number[] {
  const fromState = booked ?? [];
  if (bookedAt === null) return fromClass.length ? fromClass : fromState;
  return [...new Set([...fromClass, ...fromState])];
}

/**
 * Crédits déjà relus depuis la réservation : une lecture terminée après `bookedAt`. Elle est forcément partie après
 * la réponse du serveur, `refreshAfterBooking` annulant toute lecture plus ancienne encore en vol.
 */
export function creditsReadSince(cached: Pick<CachedQuery<unknown>, 'dataUpdatedAt' | 'fetchStatus'> | undefined, bookedAt: number): boolean {
  return !!cached && cached.fetchStatus === 'idle' && cached.dataUpdatedAt > bookedAt;
}
